import { DepressionContentPage } from "@/components/DepressionContentPage";

const DepressionTreatmentOptions = () => (
  <DepressionContentPage
    seo={{
      title:
        "Depression Treatment Options in Coral Springs, FL | Neuro Wellness TMS Centers of America",
      description:
        "Understand your depression treatment options — medication, psychotherapy, TMS, accelerated TMS, SAINT®, SPRAVATO®, and ketamine. Explore personalized, interventional approaches.",
      canonical: "/depression-treatment-options",
      keywords:
        "depression treatment options, treatment-resistant depression, TMS, SAINT, SPRAVATO, ketamine, Coral Springs FL",
    }}
    badge="Depression Treatment Hub"
    heroTitle="Understanding Your Options for Depression Treatment"
    heroSubtitle="Depression treatment is not one-size-fits-all. For some people, medication and psychotherapy provide meaningful improvement. Others continue experiencing depression despite trying one or more approaches. This page can help you understand different treatment categories and when a conversation with an interventional mental health provider may be appropriate."
    sections={[
      {
        variant: "callout",
        callout:
          "You don't have to know which treatment you need before contacting us. Start by understanding your options.",
      },
      {
        heading: "Medication",
        variant: "text",
        body: (
          <>
            <p>
              Antidepressant medications are often a first step in treating
              depression. They work by influencing certain chemicals in the
              brain involved in mood regulation. Different classes of
              antidepressants work in different ways, and what helps one person
              may not help another.
            </p>
            <p>
              Some people find meaningful improvement with medication. Others
              experience partial relief, side effects, or a response that fades
              over time. If you are currently taking medication, do not stop or
              change it without discussing it with your prescribing healthcare
              provider.
            </p>
          </>
        ),
      },
      {
        heading: "Psychotherapy",
        variant: "text",
        body: (
          <p>
            Therapy remains an important part of treatment for many patients.
            Approaches such as cognitive behavioral therapy (CBT), dialectical
            behavior therapy (DBT), and EMDR can help people understand
            patterns, develop coping strategies, and process difficult
            experiences. Therapy may be used on its own or alongside other
            treatments.
          </p>
        ),
      },
      {
        heading: "Traditional TMS",
        variant: "text",
        body: (
          <p>
            Transcranial magnetic stimulation (TMS) is a non-invasive treatment
            that uses magnetic pulses to stimulate targeted areas of the brain
            associated with mood. It does not require anesthesia and is
            performed in an outpatient setting. A traditional course typically
            involves sessions over several weeks. TMS is FDA-cleared for
            treatment-resistant depression.
          </p>
        ),
      },
      {
        heading: "Accelerated TMS",
        variant: "text",
        body: (
          <p>
            Accelerated TMS differs from traditional TMS primarily in its
            treatment schedule and protocol. Rather than spreading sessions over
            several weeks, accelerated protocols deliver multiple sessions per
            day over a shorter period. Individual results vary, and eligibility
            is determined through clinical evaluation.
          </p>
        ),
      },
      {
        heading: "SAINT® — Individualized Accelerated TMS",
        variant: "text",
        body: (
          <p>
            SAINT® (Stanford Accelerated Intelligent Neuromodulation Therapy) is
            an FDA-cleared accelerated TMS protocol that uses functional MRI to
            individually map and target the treatment area. The protocol
            compresses treatment into five consecutive days. Whether SAINT may
            be appropriate is a clinical determination made during evaluation.
          </p>
        ),
      },
      {
        heading: "SPRAVATO® (Esketamine)",
        variant: "text",
        body: (
          <p>
            SPRAVATO® is a prescription nasal spray containing esketamine,
            FDA-approved for adults with treatment-resistant depression. It is
            administered under medical supervision at a certified treatment
            center, with a required monitoring period afterward. It works
            through a different mechanism than oral antidepressants.
          </p>
        ),
      },
      {
        heading: "Ketamine Treatment",
        variant: "text",
        body: (
          <p>
            Neuro Wellness TMS Centers of America offers IV ketamine infusions
            and ketamine-assisted psychotherapy. IV ketamine is administered as
            an infusion in a clinical setting. Whether ketamine may be
            appropriate depends on your clinical history and is determined
            during consultation.
          </p>
        ),
      },
      {
        heading: "How Treatment Decisions Are Made",
        variant: "text",
        body: (
          <p>
            Diagnosis, symptoms, previous treatments, medications, health
            history, clinical evaluation, treatment schedule, patient
            preferences, and insurance considerations may all influence
            recommendations. The goal of a consultation is to understand your
            full picture before discussing which option may be worth
            considering.
          </p>
        ),
      },
    ]}
    relatedLinks={[
      {
        title: "Compare Depression Treatments",
        href: "/compare-treatments",
      },
      {
        title: "When Antidepressants Aren't Enough",
        href: "/when-antidepressants-dont-work",
      },
      { title: "SAINT® Accelerated TMS", href: "/saint" },
      { title: "SPRAVATO® Overview", href: "/services/spravato" },
      {
        title: "What Happens During a Consultation",
        href: "/depression-treatment-consultation",
      },
      { title: "Depression Treatment FAQ", href: "/depression-treatment-faq" },
    ]}
    finalCtaText="Explore your treatment options with our team. No commitment — just a conversation about what may be appropriate for you."
  />
);

export default DepressionTreatmentOptions;
